/**
 * Derives the y-webrtc room identity for a document from a shared `roomSecret`
 * = SHA-256(projectName · password) (computed at unlock, see
 * src/data/projects.ts cacheRoomSecret). Deriving from the password — rather
 * than the project's random per-creation encryption key — means anyone who
 * opens the same project name with the same password joins the same room, even
 * if their local copies were created independently and don't share a data key.
 *
 *  - `password` = the roomSecret. Passed to WebrtcProvider's `password` option,
 *    which end-to-end-encrypts all room traffic, so a peer who guesses the room
 *    name still can't read anything without the same project password.
 *  - `room` = "yaqat-" + SHA-256(projectName · fileId · secret). Hashed so the
 *    signaling server never sees the project/file slugs. Per-document, because
 *    each document is its own ProseMirror doc → its own Y.Doc.
 */
import Cipher from "../data/crypto/cipher";

export interface RoomIdentity {
  room: string;
  password: string;
}

export async function deriveRoom(
  projectName: string,
  fileId: string,
  roomSecret: string
): Promise<RoomIdentity> {
  const room = "yaqat-" + (await Cipher.hash(`${projectName}\n${fileId}\n${roomSecret}`));
  return { room, password: roomSecret };
}
